import {colorSwatchMapping, materialThumbnailMapping} from '@amblique/king-living/app/constants/site-config';
import {
  getStickyChromeBottomPx,
  getStickyChromeScrollMarginPx,
  getStickyHeaderHeightPx,
} from '@amblique/king-living/app/utils/sticky-chrome';
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils';

export {
  getStickyChromeBottomPx,
  getStickyChromeOverlapBottomPx,
  getStickyChromeScrollMarginPx,
  getStickyHeaderHeightPx,
} from '@amblique/king-living/app/utils/sticky-chrome';

// Re-export all vendor utils
export * from '@salesforce/retail-react-app/app/utils/utils';

// Sticky header / anchor scroll — `--header-height` is set on `html` in `_app`
const SCROLL_SETTLE_FALLBACK_MS = 1000;
/** While body height keeps changing, re-align anchor (debounced). Hard stop after `followMs`. */
const SCROLL_ANCHOR_FOLLOW_DEFAULT_MS = 12000;
const SCROLL_ANCHOR_FOLLOW_DEBOUNCE_MS = 120;
const SCROLL_ANCHOR_FOLLOW_MIN_DELTA_PX = 6;
/** After at least one corrective scroll, disconnect if layout stays quiet (no resize) this long. */
const SCROLL_ANCHOR_FOLLOW_QUIET_DISCONNECT_MS = 550;

let programmaticAnchorFollowCleanup = null;

/** Ends body-resize anchor follow from {@link scrollToWithHeaderOffset} `recheckAfterLayout`. Safe to call anytime. */
export function teardownProgrammaticAnchorFollow() {
  if (programmaticAnchorFollowCleanup) {
    programmaticAnchorFollowCleanup();
    programmaticAnchorFollowCleanup = null;
  }
}

/**
 * Normalizes PWA locale (e.g. 'fr-CA') to SFCC format (e.g. 'fr_CA').
 * Use when passing locale to SFCC/Utils API (e.g. email, password reset callback).
 *
 * @param {string} [locale] - Locale from useIntl or similar (e.g. 'fr-CA', 'en-CA').
 * @returns {string} SFCC-style locale or empty string.
 *
 * @example
 * toSfccLocale('fr-CA')  // 'fr_CA'
 * toSfccLocale('en-CA') // 'en_CA'
 * toSfccLocale(null)    // ''
 */
export const toSfccLocale = (locale) => (locale ? String(locale).replace('-', '_') : '');

/**
 * True when pathname matches a route after configureRoutes adds site/locale prefixes
 * (e.g. /kl-au/passwordless-login-landing vs constant /passwordless-login-landing).
 *
 * @param {string} pathname - location.pathname
 * @param {string} routePath - e.g. PASSWORDLESS_LOGIN_LANDING_PATH
 * @returns {boolean}
 */
export function pathnameMatchesAppRoute(pathname, routePath) {
  if (!pathname || !routePath) {
    return false;
  }

  return pathname === routePath || pathname.endsWith(routePath);
}

/**
 * Ensures a value is an array, returns empty array if not.
 */
export const ensureArray = (value) => {
  return Array.isArray(value) ? value : [];
};

/**
 * Checks if a value is a non-empty array.
 */
export const hasItems = (value) => {
  return Array.isArray(value) && value.length > 0;
};

/**
 * Plain single-line text from an HTML snippet (e.g. product shortDescription).
 * Strips tags and collapses whitespace — for UI labels, not a full HTML sanitizer.
 *
 * @param {string} [html]
 * @returns {string}
 */
export function stripHtmlToPlainText(html) {
  if (html == null || typeof html !== 'string') return '';
  const withoutTags = html.replace(/<[^>]*>/g, ' ');
  const normalized = withoutTags.replace(/&nbsp;/gi, ' ').replace(/\u00a0/g, ' ');

  return normalized.replace(/\s+/g, ' ').trim();
}

/**
 * Plain label for gallery header "{label} details" when shortDescription is missing on SCAPI hits
 * (e.g. slot list) — falls back to merchandising line c_nameLine2 (e.g. material family).
 *
 * @param {Object} [product]
 * @returns {string}
 */
export function getGalleryDetailShortLabel(product) {
  if (!product) return '';
  const fromShort = stripHtmlToPlainText(product.shortDescription);
  if (fromShort) return fromShort;
  const line2 = product.c_nameLine2;
  if (line2 != null && String(line2).trim()) return String(line2).trim();

  return '';
}

function escapeBasicHtmlText(s) {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * HTML for ViewDetails short block: BM shortDescription, or plain c_nameLine2 wrapped in <p>.
 *
 * @param {Object} [product]
 * @returns {string|undefined}
 */
export function getViewDetailsDescriptionHtml(product) {
  if (!product) return undefined;
  if (product.shortDescription) return product.shortDescription;
  const line2 = product.c_nameLine2;
  if (line2 != null && String(line2).trim()) {
    return `<p>${escapeBasicHtmlText(String(line2).trim())}</p>`;
  }

  return undefined;
}

/**
 * ViewDetails heading for a child product (cover variant or option SKU).
 * Strips SFCC master-prefixed catalog `name` only when it matches "{masterName} - …".
 * Does not alter master or unrelated product names.
 *
 * @param {Object} [product]
 * @param {Object} [masterProduct]
 * @returns {string}
 */
export function getViewDetailsProductTitle(product, masterProduct) {
  const rawName = product?.name ?? '';

  if (!rawName) return '';

  const productId = product?.id ?? product?.productId;
  const masterId = masterProduct?.id ?? masterProduct?.productId;
  const masterName = masterProduct?.name;

  if (!masterName || !productId || productId === masterId) {
    return rawName;
  }

  const prefix = `${masterName} - `;

  if (rawName.indexOf(prefix) === 0) {
    return rawName.substring(prefix.length).replace(/^\s+|\s+$/g, '');
  }

  return rawName;
}

/**
 * Sort CRM address list for display: default (PersonAccount shipping) first,
 * then additional ContactPointAddress rows oldest → newest by CreatedDate (FSD 6.3).
 *
 * @param {Array<Object>} addresses - Items from `/api/crm/addresses` (may include `createdAt`)
 * @returns {Array<Object>} New sorted array
 */
export function sortCrmAddressesForDisplay(addresses) {
  if (!addresses?.length) return [];

  const time = (a) => {
    const t = a.createdAt ? Date.parse(a.createdAt) : NaN;

    return Number.isNaN(t) ? 0 : t;
  };

  return [...addresses].sort((a, b) => {
    const aDefault = a.id === 'default' || a.isDefault;
    const bDefault = b.id === 'default' || b.isDefault;
    const defaultCmp = (bDefault ? 1 : 0) - (aDefault ? 1 : 0);

    if (defaultCmp !== 0) return defaultCmp;

    const ta = time(a);
    const tb = time(b);

    if (ta !== tb) return ta - tb;

    return String(a.id || '').localeCompare(String(b.id || ''));
  });
}

/**
 * Normalize a refinement color / colour-group label to a lookup key for {@link colorSwatchMapping}.
 *
 * @param {string} label
 * @returns {string}
 */
export const normalizeColorGroupKey = (label) => {
  if (label == null || typeof label !== 'string') {
    return '';
  }

  return label
    .trim()
    .toLowerCase()
    .replace(/\s*\/\s*/g, '_')
    .replace(/[^a-z0-9_]+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_|_$/g, '');
};

/**
 * Resolve swatch image URL for a colour group label, or null if unmapped.
 * Uses {@link colorSwatchMapping} paths from site-config and getAssetUrl at call time (not at module load).
 *
 * @param {string} [colorLabel] - e.g. refinementColor from c_processedData ("Black / Dark Grey")
 * @returns {string|null}
 */
export const resolveColorSwatchUrl = (colorLabel) => {
  const slug = normalizeColorGroupKey(colorLabel);

  if (!slug) {
    return null;
  }

  let staticPath = colorSwatchMapping[slug];

  if (!staticPath) {
    const firstToken = slug.split('_')[0];

    if (firstToken) {
      staticPath = colorSwatchMapping[firstToken];
    }
  }

  if (!staticPath) {
    return null;
  }

  return getAssetUrl(staticPath);
};

/**
 * Resolve static thumbnail URL for a cover item-type tab label (e.g. Fabric, Feather), or null if unmapped.
 * Uses {@link materialThumbnailMapping} and the same key normalization as colour swatches.
 *
 * @param {string} [itemTypeLabel] - e.g. group.itemType from cover groups
 * @returns {string|null}
 */
export const resolveMaterialThumbnailUrl = (itemTypeLabel) => {
  const slug = normalizeColorGroupKey(itemTypeLabel);

  if (!slug) {
    return null;
  }

  let staticPath = materialThumbnailMapping[slug];

  if (!staticPath) {
    const firstToken = slug.split('_')[0];

    if (firstToken) {
      staticPath = materialThumbnailMapping[firstToken];
    }
  }

  if (!staticPath) {
    return null;
  }

  return getAssetUrl(staticPath);
};

/**
 * Generate URL-friendly slug from a string (e.g., store name, page title)
 * Used for creating SEO-friendly URLs like /showroom/sydney-cbd
 *
 * @param {string} text - The text to convert to a slug
 * @returns {string} URL-friendly slug
 *
 * @example
 * generateSlug('Sydney CBD') // returns 'sydney-cbd'
 * generateSlug('Annandale Outlet') // returns 'annandale-outlet'
 * generateSlug('King Living Melbourne') // returns 'king-living-melbourne'
 * generateSlug("King's & Store (Main)") // returns 'king-s-store-main'
 */
export const generateSlug = (text) => {
  if (!text) return '';

  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '-') // Replace special characters with hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Generate cookie name for "My Showroom" based on site ID
 * Each region/site has its own cookie to store the user's preferred showroom
 *
 * @param {string} siteId - The site identifier (e.g., 'kl-au', 'kl-nz', 'kl-uk')
 * @returns {string} Cookie name in format: my_showroom_{siteId}
 *
 * @example
 * getMyShowroomCookieName('kl-au') // returns 'my_showroom_kl-au'
 * getMyShowroomCookieName('kl-nz') // returns 'my_showroom_kl-nz'
 * getMyShowroomCookieName('kl-uk') // returns 'my_showroom_kl-uk'
 */
export const getMyShowroomCookieName = (siteId) => {
  if (!siteId) return 'my_showroom';

  return `my_showroom_${siteId}`;
};

/**
 * Generate cookie name for "Nearest Showroom" based on site ID
 * Stores the nearest showroom based on user's current location (cached)
 *
 * @param {string} siteId - The site identifier (e.g., 'kl-au', 'kl-nz', 'kl-uk')
 * @returns {string} Cookie name in format: nearest_showroom_{siteId}
 *
 * @example
 * getNearestShowroomCookieName('kl-au') // returns 'nearest_showroom_kl-au'
 * getNearestShowroomCookieName('kl-nz') // returns 'nearest_showroom_kl-nz'
 */
export const getNearestShowroomCookieName = (siteId) => {
  if (!siteId) return 'nearest_showroom';

  return `nearest_showroom_${siteId}`;
};

// =============================================================================
// MY SHOWROOM - Custom Event for cross-component communication
// =============================================================================

export const SHOWROOM_CHANGED_EVENT = 'kl:showroom-changed';

/**
 * Dispatch custom event when showroom selection changes
 * This allows components like Header to update without page reload
 * @param {Object|null} showroom - The new showroom data or null if cleared
 */
export const dispatchShowroomChangedEvent = (showroom) => {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent(SHOWROOM_CHANGED_EVENT, {
    detail: {showroom},
  }));
};

// =============================================================================
// MY SHOWROOM - Cookie Helpers (7 days TTL)
// =============================================================================

const MY_SHOWROOM_COOKIE_TTL_DAYS = 7;

/**
 * Get user's preferred showroom from cookie
 * @param {string} siteId - The site identifier
 * @returns {Object|null} Showroom data {id, name, slug, lat, lng} or null
 */
export const getMyShowroomFromCookie = (siteId) => {
  if (typeof document === 'undefined') return null;

  const cookieName = getMyShowroomCookieName(siteId);

  try {
    const cookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${cookieName}=`));

    if (cookie) {
      const value = cookie.split('=')[1];

      return JSON.parse(decodeURIComponent(value));
    }
  } catch (error) {
    console.error('[MyShowroom] Error reading cookie:', error);
  }

  return null;
};

/**
 * Save user's preferred showroom to cookie (7 days TTL)
 * Also dispatches a custom event for real-time UI updates
 * @param {string} siteId - The site identifier
 * @param {Object} showroom - Showroom data to save
 */
export const setMyShowroomToCookie = (siteId, showroom) => {
  if (typeof document === 'undefined' || !showroom) return;

  const cookieName = getMyShowroomCookieName(siteId);

  try {
    const value = encodeURIComponent(JSON.stringify(showroom));
    const expires = new Date();

    expires.setDate(expires.getDate() + MY_SHOWROOM_COOKIE_TTL_DAYS);

    document.cookie = `${cookieName}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

    // Dispatch event for real-time UI updates (e.g., Header)
    dispatchShowroomChangedEvent(showroom);
  } catch (error) {
    console.error('[MyShowroom] Error saving cookie:', error);
  }
};

/**
 * Parse account showroom from customer data
 * Handles both string and object formats, validates siteId
 * @param {string|Object} accountShowroomRaw - Raw showroom data from account
 * @param {string} siteId - The site identifier to validate against
 * @returns {Object|null} Parsed showroom data or null if invalid/wrong site
 */
export const parseAccountShowroom = (accountShowroomRaw, siteId) => {
  if (!accountShowroomRaw || !siteId) return null;

  try {
    const accountShowroom = typeof accountShowroomRaw === 'string' ?
      JSON.parse(accountShowroomRaw) :
      accountShowroomRaw;

    // Only use if it's for the current site
    if (accountShowroom && accountShowroom.siteId && accountShowroom.siteId !== siteId) {
      return null;
    }

    return accountShowroom;
  } catch {
    // Invalid JSON, ignore
    return null;
  }
};

/**
 * Remove user's preferred showroom cookie
 * @param {string} siteId - The site identifier
 */
export const clearMyShowroomCookie = (siteId) => {
  if (typeof document === 'undefined') return;

  const cookieName = getMyShowroomCookieName(siteId);

  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

// =============================================================================
// SHOWROOM POPUP - sessionStorage Helpers
// =============================================================================

/**
 * Get sessionStorage key for popup dismissed state
 * @param {string} siteId - The site identifier
 * @returns {string} Key name
 */
export const getPopupDismissedKey = (siteId) => {
  if (!siteId) return 'kl_showroom_popup_dismissed';

  return `kl_showroom_popup_dismissed_${siteId}`;
};

/**
 * Check if showroom popup was dismissed this session
 * @param {string} siteId - The site identifier
 * @returns {boolean}
 */
export const isShowroomPopupDismissed = (siteId) => {
  if (typeof sessionStorage === 'undefined') return false;

  return sessionStorage.getItem(getPopupDismissedKey(siteId)) === 'true';
};

/**
 * Mark showroom popup as dismissed
 * @param {string} siteId - The site identifier
 */
export const setShowroomPopupDismissed = (siteId) => {
  if (typeof sessionStorage === 'undefined') return;

  sessionStorage.setItem(getPopupDismissedKey(siteId), 'true');
};

// =============================================================================
// LOCALE PREFERENCE - Cookie Helpers (365 days TTL)
// =============================================================================

const LOCALE_COOKIE_TTL_DAYS = 365;

export const LOCALE_CHANGED_EVENT = 'kl:locale-changed';

/**
 * Generate cookie name for preferred locale based on site ID
 * Each region/site has its own cookie to store the user's preferred locale
 *
 * @param {string} siteId - The site identifier (e.g., 'kl-au', 'kl-ca')
 * @returns {string} Cookie name in format: preferred_locale_{siteId}
 *
 * @example
 * getPreferredLocaleCookieName('kl-ca') // returns 'preferred_locale_kl-ca'
 */
export const getPreferredLocaleCookieName = (siteId) => {
  if (!siteId) return 'preferred_locale';

  return `preferred_locale_${siteId}`;
};

/**
 * Get user's preferred locale from cookie
 * @param {string} siteId - The site identifier
 * @returns {string|null} Locale ID (e.g., 'en-CA', 'fr-CA') or null
 */
export const getPreferredLocaleFromCookie = (siteId) => {
  if (typeof document === 'undefined') return null;

  const cookieName = getPreferredLocaleCookieName(siteId);

  try {
    const cookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${cookieName}=`));

    if (cookie) {
      const value = cookie.split('=')[1];

      return decodeURIComponent(value);
    }
  } catch (error) {
    console.error('[PreferredLocale] Error reading cookie:', error);
  }

  return null;
};

/**
 * Save user's preferred locale to cookie (365 days TTL)
 * Also dispatches a custom event for real-time UI updates
 * @param {string} siteId - The site identifier
 * @param {string} localeId - Locale ID to save (e.g., 'en-CA', 'fr-CA')
 */
export const setPreferredLocaleToCookie = (siteId, localeId) => {
  if (typeof document === 'undefined' || !localeId) return;

  const cookieName = getPreferredLocaleCookieName(siteId);

  try {
    const value = encodeURIComponent(localeId);
    const expires = new Date();

    expires.setDate(expires.getDate() + LOCALE_COOKIE_TTL_DAYS);

    document.cookie = `${cookieName}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

    // Dispatch event for real-time UI updates
    dispatchLocaleChangedEvent(localeId);
  } catch (error) {
    console.error('[PreferredLocale] Error saving cookie:', error);
  }
};

/**
 * Remove user's preferred locale cookie
 * @param {string} siteId - The site identifier
 */
export const clearPreferredLocaleCookie = (siteId) => {
  if (typeof document === 'undefined') return;

  const cookieName = getPreferredLocaleCookieName(siteId);

  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

// =============================================================================
// GEO REDIRECT (client geolocation hint) — Cookie Helpers (7 days TTL)
// =============================================================================

const GEO_REDIRECT_COOKIE_TTL_DAYS = 7;

/**
 * Cookie name for last geo-redirect evaluation per site (detected country cache).
 * @param {string} siteId - Site id (e.g. kl-au)
 * @returns {string}
 */
export const getGeoRedirectCookieName = (siteId) => {
  if (!siteId) return 'kl_geo_redirect';

  return `kl_geo_redirect_${siteId}`;
};

export const clearGeoRedirectCookie = (siteId) => {
  if (typeof document === 'undefined' || !siteId) return;

  const cookieName = getGeoRedirectCookieName(siteId);

  document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

/**
 * @returns {{ timestamp: string, country: string|null }|null}
 */
export const getGeoRedirectDataFromCookie = (siteId) => {
  if (typeof document === 'undefined' || !siteId) return null;

  const cookieName = getGeoRedirectCookieName(siteId);

  try {
    const cookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${cookieName}=`));

    if (!cookie) return null;

    const raw = decodeURIComponent(cookie.split('=').slice(1).join('='));
    const data = JSON.parse(raw);

    if (!data?.timestamp) return null;

    const expiryTime = new Date(data.timestamp);

    expiryTime.setDate(expiryTime.getDate() + GEO_REDIRECT_COOKIE_TTL_DAYS);

    if (new Date() > expiryTime) {
      clearGeoRedirectCookie(siteId);

      return null;
    }

    return {
      timestamp: data.timestamp,
      country: data.country != null ? String(data.country).toUpperCase() : null,
    };
  } catch (error) {
    console.error('[GeoRedirect] Error reading cookie:', error);

    return null;
  }
};

/**
 * Persist geo-redirect check result (avoids repeat calls within TTL).
 * @param {string} siteId
 * @param {string|null} countryCode - ISO country detected, or null if unknown / no redirect
 */
export const setGeoRedirectDataToCookie = (siteId, countryCode) => {
  if (typeof document === 'undefined' || !siteId) return;

  const cookieName = getGeoRedirectCookieName(siteId);

  try {
    const payload = JSON.stringify({
      timestamp: new Date().toISOString(),
      country: countryCode ? String(countryCode).toUpperCase() : null,
    });
    const value = encodeURIComponent(payload);
    const expires = new Date();

    expires.setDate(expires.getDate() + GEO_REDIRECT_COOKIE_TTL_DAYS);

    document.cookie = `${cookieName}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  } catch (error) {
    console.error('[GeoRedirect] Error saving cookie:', error);
  }
};

const REGION_MODAL_DISMISS_TTL_DAYS = 30;

/**
 * User chose “stay on this site” for region suggestion; suppress modal for a while.
 * @param {string} siteId
 * @returns {string}
 */
export const getRegionModalDismissCookieName = (siteId) => {
  if (!siteId) return 'kl_region_modal_dismiss';

  return `kl_region_modal_dismiss_${siteId}`;
};

export const isRegionModalDismissed = (siteId) => {
  if (typeof document === 'undefined' || !siteId) return false;

  const cookieName = getRegionModalDismissCookieName(siteId);

  try {
    const cookie = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${cookieName}=`));

    return cookie === `${cookieName}=1`;
  } catch (error) {
    console.error('[RegionModal] Error reading dismiss cookie:', error);

    return false;
  }
};

export const setRegionModalDismissed = (siteId) => {
  if (typeof document === 'undefined' || !siteId) return;

  const cookieName = getRegionModalDismissCookieName(siteId);

  try {
    const expires = new Date();

    expires.setDate(expires.getDate() + REGION_MODAL_DISMISS_TTL_DAYS);

    document.cookie = `${cookieName}=1; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
  } catch (error) {
    console.error('[RegionModal] Error saving dismiss cookie:', error);
  }
};

/**
 * Dispatch custom event when locale preference changes
 * This allows components to react to locale changes without page reload
 * @param {string} localeId - The new locale ID
 */
export const dispatchLocaleChangedEvent = (localeId) => {
  if (typeof window === 'undefined') return;

  window.dispatchEvent(new CustomEvent(LOCALE_CHANGED_EVENT, {
    detail: {localeId},
  }));
};

// =============================================================================
// HEADER STYLE - Route-based header style configuration from Contentful
// =============================================================================

import {
  DEFAULT_HEADER_STATE,
  HEADER_DESIGN_STATE,
} from '@amblique/king-living/app/constants/header';
import {removeSiteLocaleFromPath} from '@salesforce/retail-react-app/app/utils/url';

/**
 * Valid header types for validation
 * @type {string[]}
 */
const VALID_HEADER_TYPES = Object.values(HEADER_DESIGN_STATE);

/**
 * Normalize path by removing trailing slashes (preserves root path "/")
 * @param {string} path - Path to normalize
 * @returns {string} Normalized path
 */
const normalizePath = (path) => {
  if (!path || typeof path !== 'string') return '/';

  // Remove trailing slashes but preserve root "/"
  const normalized = path.replace(/\/+$/, '');

  return normalized || '/';
};

/**
 * Homepage URL aliases — Contentful slug is `home` but the live route is `/`.
 * @type {Set<string>}
 */
const HOME_PATH_ALIASES = new Set(['/', '/home']);

const isHomeAliasPath = (path) => HOME_PATH_ALIASES.has(normalizePath(path));

/**
 * Check if a path matches a pattern (supports wildcards)
 *
 * @param {string} pattern - URL pattern (e.g., "/account/*", "/checkout")
 * @param {string} currentPath - Current browser path
 * @returns {boolean} Whether the path matches
 *
 * @example
 * matchPath('/account/*', '/account/profile') // true
 * matchPath('/checkout', '/checkout') // true
 * matchPath('/account/*', '/account') // true
 */
const matchPath = (pattern, currentPath) => {
  const normalizedPattern = normalizePath(pattern);
  const normalizedPath = normalizePath(currentPath);

  // `/home` in Contentful config matches live homepage `/` (and vice versa)
  if (isHomeAliasPath(normalizedPattern) && isHomeAliasPath(normalizedPath)) {
    return true;
  }

  // Exact match (handles root "/" and other paths)
  if (normalizedPattern === normalizedPath) {
    return true;
  }

  // Wildcard match: "/account/*" matches "/account" and "/account/anything"
  if (normalizedPattern.endsWith('/*')) {
    const basePattern = normalizedPattern.slice(0, -2) || '/';

    return normalizedPath === basePattern || normalizedPath.startsWith(basePattern + '/');
  }

  return false;
};

/**
 * Parse headerStyle config from Contentful
 * Handles both:
 * - Already parsed object (Contentful JSON type returns parsed object)
 * - JSON string (if stored as Long Text type)
 *
 * @param {Object|string|null} headerStyleData - Config data from Contentful field
 * @returns {Object|null} Config object or null if invalid
 */
const parseHeaderStyleConfig = (headerStyleData) => {
  if (!headerStyleData) {
    return null;
  }

  // If already an object (Contentful JSON type), return directly
  if (typeof headerStyleData === 'object' && headerStyleData !== null) {
    return headerStyleData;
  }

  // If string (Long Text type), parse it
  if (typeof headerStyleData === 'string') {
    try {
      const config = JSON.parse(headerStyleData);

      if (!config || typeof config !== 'object') {
        return null;
      }

      return config;
    } catch (error) {
      console.warn('[getHeaderStyleForPath] Failed to parse headerStyle JSON:', error.message);

      return null;
    }
  }

  return null;
};

/**
 * Get header style type for current path from Contentful config
 *
 * Priority:
 * 1. Matching page pattern from config.pages array
 * 2. Default type from config.default
 * 3. Fallback to DEFAULT_HEADER_STATE constant
 *
 * @param {Object|string|null} headerStyleData - Config from Contentful headerStyle field
 * @param {string} currentPath - Current browser pathname
 * @returns {string} Header type ('solid' | 'gradient' | 'transparent')
 *
 * @example
 * const headerType = getHeaderStyleForPath(contentfulHeader.headerStyle, location.pathname);
 */
export const getHeaderStyleForPath = (headerStyleData, currentPath) => {
  const config = parseHeaderStyleConfig(headerStyleData);

  if (!config) {
    return DEFAULT_HEADER_STATE;
  }

  const {default: defaultType = DEFAULT_HEADER_STATE, pages = []} = config;
  const matchPathname = normalizePath(
    typeof currentPath === 'string' ? removeSiteLocaleFromPath(currentPath) : '/'
  );

  if (!Array.isArray(pages)) {
    return VALID_HEADER_TYPES.includes(defaultType) ? defaultType : DEFAULT_HEADER_STATE;
  }

  // Find matching page config
  const matchedPage = pages.find((page) => {
    if (!page || typeof page !== 'object' || !page.url) {
      return false;
    }

    return matchPath(page.url, matchPathname);
  });

  const resultType = matchedPage?.type || defaultType;

  return VALID_HEADER_TYPES.includes(resultType) ? resultType : DEFAULT_HEADER_STATE;
};

/**
 * Check if a header style is an "overlay" mode
 * Overlay modes cause the header to float over content (transparent/gradient)
 * In overlay mode, the main content extends behind the header
 *
 * @param {string} headerStyle - Header style type ('solid', 'gradient', 'transparent')
 * @returns {boolean} Whether the style is overlay mode
 *
 * @example
 * isHeaderOverlayMode('gradient') // true
 * isHeaderOverlayMode('transparent') // true
 * isHeaderOverlayMode('solid') // false
 */
export const isHeaderOverlayMode = (headerStyle) => {
  return headerStyle === HEADER_DESIGN_STATE.TRANSPARENT ||
    headerStyle === HEADER_DESIGN_STATE.GRADIENT;
};

/**
 * Set from {@link HeaderProvider} when PDP toggles `setHideOnScroll`. Drives programmatic scroll
 * margin: scroll **down** → treat chrome as 0 (header will hide); scroll **up** → use nominal
 * `--header-height` so targets land below the bar when it reappears.
 */
let pdpHideOnScrollProgrammaticScroll = false;

export const syncPdpHideOnScrollProgrammaticScroll = (enabled) => {
  pdpHideOnScrollProgrammaticScroll = !!enabled;
};

/**
 * Total px to subtract from `(elementTop + scrollY)` for smooth/instant scroll alignment.
 *
 * @param {number} scrollY
 * @param {number} elementViewportTop - `getBoundingClientRect().top`
 * @param {number} extraOffsetPx
 * @returns {number}
 */
const getProgrammaticStickyScrollMarginPx = (scrollY, elementViewportTop, extraOffsetPx) => {
  if (!pdpHideOnScrollProgrammaticScroll) {
    return getStickyChromeScrollMarginPx() + extraOffsetPx;
  }

  const docY = elementViewportTop + scrollY;
  const targetIfDown = docY - extraOffsetPx;
  const scrollingDown = targetIfDown > scrollY + 0.5;

  if (scrollingDown) {
    return extraOffsetPx;
  }

  return getStickyHeaderHeightPx() + extraOffsetPx;
};

/**
 * Parses `scroll-margin-top` from computed styles. Browsers only apply it to `scrollIntoView` and
 * in-page navigation — not to `window.scrollTo` — so {@link scrollToWithHeaderOffset} must include it.
 *
 * @param {HTMLElement | null | undefined} element
 * @returns {number}
 */
export function getElementScrollMarginTopPx(element) {
  if (!element || typeof window === 'undefined') {
    return 0;
  }

  const raw = window.getComputedStyle(element).scrollMarginTop;
  const n = Number.parseFloat(raw);

  return Number.isFinite(n) ? n : 0;
}

const BODY_SCROLL_LOCK_STYLE_PROPS = ['position', 'top', 'left', 'right', 'width', 'overflow'];

/**
 * Instant `window.scrollTo` top to align `element` below sticky chrome.
 * On PDP hide-on-scroll, same rules as {@link scrollToWithHeaderOffset} (down = no header band; up = nominal height).
 *
 * @param {HTMLElement | null} element
 * @param {number} scrollYBefore - `window.scrollY` before scrolling
 * @returns {number | null} scroll top, or null if `element` is missing
 */
export const computeScrollTopBelowStickyHeader = (element, scrollYBefore) => {
  if (!element) return null;

  const rect = element.getBoundingClientRect();
  const scrollMarginPx = getProgrammaticStickyScrollMarginPx(
    scrollYBefore,
    rect.top,
    getElementScrollMarginTopPx(element)
  );

  return Math.max(0, rect.top + scrollYBefore - scrollMarginPx);
};

/**
 * Freezes the viewport by fixing `document.body`. Prefer over overflow-only locks when nested
 * scroll regions (e.g. 100vh flex + overflow-y auto) can make `window.scrollY` jump.
 *
 * @param {number} scrollYVisual - `window.scrollY` after any `scrollTo` to preserve visually
 */
export const lockBodyScrollFreeze = (scrollYVisual) => {
  if (typeof document === 'undefined') return;

  const body = document.body;

  body.style.position = 'fixed';
  body.style.top = `-${scrollYVisual}px`;
  body.style.left = '0';
  body.style.right = '0';
  body.style.width = '100%';
  body.style.overflow = 'hidden';

  // Sync header hide-on-scroll / sticky state: `window.scrollY` is often 0 while frozen.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('scroll'));
  }
};

/** Reverses {@link lockBodyScrollFreeze}. */
export const unlockBodyScrollFreeze = () => {
  if (typeof document === 'undefined') return;

  const body = document.body;

  for (const prop of BODY_SCROLL_LOCK_STYLE_PROPS) {
    body.style.removeProperty(prop);
  }

  document.documentElement.style.removeProperty('overflow');
};

/**
 * Restore page scroll after a mobile drawer closes.
 * Scrolls synchronously first to avoid a visible flash to top after body unlock,
 * then retries on the next frame(s) if iOS/Chakra did not land on the target.
 *
 * @param {number} scrollY
 * @returns {() => void} Cancel pending restoration retries
 */
export const restoreScrollAfterBodyUnlock = (scrollY) => {
  if (typeof window === 'undefined') {
    return () => {};
  }

  const targetY = typeof scrollY === 'number' ? scrollY : 0;

  try {
    window.scrollTo(0, targetY);
  } catch {
    // Ignore: scrollTo may fail in test env / iframe restrictions
  }

  let rafId1 = null;
  let rafId2 = null;

  const retryIfNeeded = () => {
    const current = window.scrollY ?? document.documentElement.scrollTop ?? 0;

    if (Math.abs(current - targetY) <= 2) return;

    try {
      window.scrollTo(0, targetY);
    } catch {
      // Ignore
    }
  };

  rafId1 = requestAnimationFrame(() => {
    retryIfNeeded();
    rafId2 = requestAnimationFrame(retryIfNeeded);
  });

  return () => {
    if (rafId1 !== null) cancelAnimationFrame(rafId1);
    if (rafId2 !== null) cancelAnimationFrame(rafId2);
  };
};

/**
 * Unlock a {@link lockBodyScrollFreeze} lock and restore scroll in one synchronous step
 * so the page does not paint at scrollY=0 between unlock and restore.
 *
 * @param {number} [scrollY] - Saved position; falls back to `body.style.top` from the lock
 * @returns {() => void} Cancel pending restoration retries
 */
export const unlockBodyScrollFreezeAndRestore = (scrollY) => {
  if (typeof document === 'undefined') {
    return () => {};
  }

  const body = document.body;
  const parsedTop = Math.abs(parseInt(body.style.top || '0', 10) || 0);
  const targetY = typeof scrollY === 'number' && scrollY >= 0 ? scrollY : parsedTop;

  unlockBodyScrollFreeze();

  document.documentElement.scrollTop = targetY;
  document.body.scrollTop = targetY;

  return restoreScrollAfterBodyUnlock(targetY);
};

/**
 * After smooth scroll, keep the anchor under the sticky header whenever `document.body` resizes
 * (delivery, images, fonts, etc.). Debounced to limit jank; stops after `followMs` or when target
 * disconnects. A new {@link scrollToWithHeaderOffset} cancels the previous follow session.
 *
 * @param {HTMLElement} target
 * @param {number} extraOffsetPx
 * @param {boolean | {followMs?: number, debounceMs?: number}} recheckAfterLayout
 */
const attachProgrammaticAnchorLayoutFollow = (target, extraOffsetPx, recheckAfterLayout) => {
  teardownProgrammaticAnchorFollow();

  if (typeof document === 'undefined' || typeof window === 'undefined' || !target?.isConnected) {
    return;
  }

  const followMs =
    typeof recheckAfterLayout === 'object' && recheckAfterLayout != null ?
      (recheckAfterLayout.followMs ?? SCROLL_ANCHOR_FOLLOW_DEFAULT_MS) :
      SCROLL_ANCHOR_FOLLOW_DEFAULT_MS;
  const debounceMs =
    typeof recheckAfterLayout === 'object' && recheckAfterLayout != null ?
      (recheckAfterLayout.debounceMs ?? SCROLL_ANCHOR_FOLLOW_DEBOUNCE_MS) :
      SCROLL_ANCHOR_FOLLOW_DEBOUNCE_MS;

  if (followMs <= 0) return;

  let ro;
  let debounceTimer;
  let endTimer;
  let quietDisconnectTimer;
  let active = true;
  /** Once we corrected scroll at least once, we can end the session early when resizes stop. */
  let didApplyScroll = false;

  const wheelListenerOpts = {capture: true, passive: true};

  /** Any real user gesture (accordion expand, scroll, tab, etc.) should stop re-pinning the anchor. */
  const onUserGestureEndsFollow = () => {
    cleanup();
  };

  const cleanup = () => {
    if (!active) return;
    active = false;
    ro?.disconnect();
    window.clearTimeout(debounceTimer);
    window.clearTimeout(endTimer);
    window.clearTimeout(quietDisconnectTimer);
    document.removeEventListener('pointerdown', onUserGestureEndsFollow, true);
    document.removeEventListener('click', onUserGestureEndsFollow, true);
    window.removeEventListener('wheel', onUserGestureEndsFollow, wheelListenerOpts);
    window.removeEventListener('touchmove', onUserGestureEndsFollow, wheelListenerOpts);

    if (programmaticAnchorFollowCleanup === cleanup) {
      programmaticAnchorFollowCleanup = null;
    }
  };

  const snapIfNeeded = () => {
    if (!active) return;

    if (!target.isConnected) {
      cleanup();

      return;
    }

    const elementTop = target.getBoundingClientRect().top;
    const scrollY = window.scrollY;
    // Follow-up snaps only: do not use hide-on-scroll *directional* margin (down vs up). Tiny layout
    // shifts (Swiper, images, fonts) can flip that branch and jump scrollTop by ~header height — feels
    // like a sudden scroll-up + header popping in (e.g. Want it Sooner after content settles).
    const scrollMargin =
      getStickyChromeBottomPx() + extraOffsetPx + getElementScrollMarginTopPx(target);
    const nextTop = Math.max(0, elementTop + scrollY - scrollMargin);
    const drift = Math.abs(window.scrollY - nextTop);

    if (drift >= SCROLL_ANCHOR_FOLLOW_MIN_DELTA_PX) {
      window.clearTimeout(quietDisconnectTimer);
      window.scrollTo({top: nextTop, behavior: 'auto'});
      didApplyScroll = true;

      return;
    }

    if (didApplyScroll) {
      window.clearTimeout(quietDisconnectTimer);
      quietDisconnectTimer = window.setTimeout(cleanup, SCROLL_ANCHOR_FOLLOW_QUIET_DISCONNECT_MS);
    }
  };

  const scheduleSnap = () => {
    window.clearTimeout(debounceTimer);
    window.clearTimeout(quietDisconnectTimer);
    debounceTimer = window.setTimeout(snapIfNeeded, debounceMs);
  };

  if (typeof ResizeObserver === 'undefined') {
    window.setTimeout(snapIfNeeded, debounceMs);
    window.setTimeout(snapIfNeeded, Math.min(followMs, 900));

    return;
  }

  ro = new ResizeObserver(scheduleSnap);
  ro.observe(document.body);
  endTimer = window.setTimeout(cleanup, followMs);
  programmaticAnchorFollowCleanup = cleanup;
  document.addEventListener('pointerdown', onUserGestureEndsFollow, true);
  document.addEventListener('click', onUserGestureEndsFollow, true);
  window.addEventListener('wheel', onUserGestureEndsFollow, wheelListenerOpts);
  window.addEventListener('touchmove', onUserGestureEndsFollow, wheelListenerOpts);
  scheduleSnap();
};

/**
 * After programmatic smooth scroll, re-measure using **live** sticky chrome ({@link getStickyChromeBottomPx}).
 * Directional hide-on-scroll heuristics can disagree with the real header (fast Done, drawer close, transition
 * mid-scroll). These snaps converge once `data-header-hidden` matches the final gesture.
 *
 * @param {HTMLElement} target
 * @param {number} extraOffsetPx - Caller offset only (css scroll-margin is read from `target`).
 * @param {true | {behavior?: string, delaysMs?: number[]}} [config]
 */
const scheduleLiveChromeResnaps = (target, extraOffsetPx, config = true) => {
  if (typeof window === 'undefined' || !target?.isConnected) {
    return;
  }

  const behavior =
    typeof config === 'object' && config != null && 'behavior' in config ?
      config.behavior ?? 'auto' :
      'auto';
  const delaysMs =
    typeof config === 'object' && config != null && Array.isArray(config.delaysMs) ?
      config.delaysMs :
      [48, 120, 220, 380, 600, 900, 1400];

  const snap = () => {
    if (!target.isConnected) {
      return;
    }

    const css = getElementScrollMarginTopPx(target);
    const rect = target.getBoundingClientRect();
    const sy = window.scrollY;
    const margin = getStickyChromeBottomPx() + extraOffsetPx + css;
    const nextTop = Math.max(0, rect.top + sy - margin);

    if (Math.abs(sy - nextTop) >= SCROLL_ANCHOR_FOLLOW_MIN_DELTA_PX) {
      window.scrollTo({top: nextTop, behavior});
    }
  };

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(snap);
  });

  for (const ms of delaysMs) {
    window.setTimeout(snap, ms);
  }
};

/**
 * Scrolls an element so it sits below sticky chrome. On PDP hide-on-scroll, downward scrolls use
 * **no** header margin (viewport top = 0); upward scrolls use nominal header height.
 *
 * @param {HTMLElement | null} target
 * @param {() => void} [onComplete]
 * @param {{extraOffsetPx?: number, recheckAfterLayout?: boolean | {followMs?: number, debounceMs?: number}, syncLiveChromeAfterSmoothScroll?: boolean | {behavior?: string, delaysMs?: number[]}, scrollBehavior?: 'auto' | 'smooth'}} [options]
 * @param {'auto' | 'smooth'} [options.scrollBehavior] `auto` avoids racing hide-on-scroll + layout (recommended for post-modal anchors). Default `smooth`.
 * @param {boolean | {followMs?: number, debounceMs?: number}} [options.recheckAfterLayout] After smooth scroll ends,
 *   watch `document.body` for size changes and re-snap the anchor (debounced) for `followMs` (default 12s).
 *   Ends early on user gesture (pointer, click, wheel, touchmove). Cancels any previous follow when starting a new
 *   programmatic scroll. Pass `true` for defaults.
 * @param {boolean | {behavior?: string, delaysMs?: number[]}} [options.syncLiveChromeAfterSmoothScroll] After
 *   `scrollend`, re-snap using live chrome (not directional heuristics) on rAF + delayed ticks.
 * @returns {boolean} `true` when a valid target was provided and `scrollTo` was called
 */
export const scrollToWithHeaderOffset = (target, onComplete, options = {}) => {
  const {
    extraOffsetPx = 0,
    recheckAfterLayout,
    syncLiveChromeAfterSmoothScroll,
    scrollBehavior = 'smooth',
  } = options ?? {};

  if (typeof document === 'undefined' || typeof window === 'undefined') {
    onComplete?.();

    return false;
  }

  if (!target) {
    onComplete?.();

    return false;
  }

  teardownProgrammaticAnchorFollow();

  const cssAnchorScrollMarginTop = getElementScrollMarginTopPx(target);
  const elementTop = target.getBoundingClientRect().top;
  const scrollY = window.scrollY;
  const scrollMargin = getProgrammaticStickyScrollMarginPx(
    scrollY,
    elementTop,
    extraOffsetPx + cssAnchorScrollMarginTop
  );
  const targetTop = Math.max(0, elementTop + scrollY - scrollMargin);

  window.scrollTo({
    top: targetTop,
    behavior: scrollBehavior,
  });

  const runAfterScrollEnd = () => {
    if (recheckAfterLayout && target.isConnected) {
      attachProgrammaticAnchorLayoutFollow(target, extraOffsetPx, recheckAfterLayout);
    }

    onComplete?.();
  };

  if (syncLiveChromeAfterSmoothScroll && target.isConnected) {
    if (scrollBehavior === 'auto') {
      scheduleLiveChromeResnaps(target, extraOffsetPx, syncLiveChromeAfterSmoothScroll);
    } else {
      const waveAfterScroll = () => {
        if (target.isConnected) {
          scheduleLiveChromeResnaps(target, extraOffsetPx, syncLiveChromeAfterSmoothScroll);
        }
      };

      scheduleAfterSmoothScroll(waveAfterScroll);
      window.setTimeout(waveAfterScroll, 950);
    }
  }

  const needsFinish = onComplete != null || recheckAfterLayout;

  if (needsFinish) {
    if (scrollBehavior === 'auto') {
      let done = false;

      const finish = () => {
        if (done) return;
        done = true;
        runAfterScrollEnd();
      };

      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(finish);
      });
      document.addEventListener('scrollend', finish, {once: true});
      window.setTimeout(finish, 220);
    } else {
      scheduleAfterSmoothScroll(runAfterScrollEnd);
    }
  }

  return true;
};

/**
 * @param {() => void} callback
 */
const scheduleAfterSmoothScroll = (callback) => {
  if (typeof callback !== 'function' || typeof document === 'undefined' || typeof window === 'undefined') {
    return;
  }

  let finished = false;
  let fallbackId;

  const finish = () => {
    if (finished) return;
    finished = true;
    document.removeEventListener('scrollend', onScrollEnd);
    window.clearTimeout(fallbackId);
    callback();
  };

  const onScrollEnd = () => {
    finish();
  };

  document.addEventListener('scrollend', onScrollEnd, {once: true});
  fallbackId = window.setTimeout(finish, SCROLL_SETTLE_FALLBACK_MS);
};

/** PDP price-breakdown anchors — follow layout while set cards / accordions expand. */
export const PDP_PRICE_BREAKDOWN_SCROLL_OPTIONS = {
  recheckAfterLayout: {debounceMs: 50, followMs: 12000},
  syncLiveChromeAfterSmoothScroll: true,
};

/**
 * True when `selector` is a single hash ID anchor (e.g. `#5oVvNYNdXlpKqo8CqWtVUC`).
 * Compound selectors (`#id.class`, `#a, #b`) must use `querySelector`.
 *
 * @param {string} selector
 * @returns {boolean}
 */
const isHashIdSelector = (selector) => {
  return (
    selector.startsWith('#') &&
    selector.length > 1 &&
    !/[\s,>+~[\].:#]/.test(selector.slice(1))
  );
};

/**
 * Resolve a scroll target from a selector string.
 * Hash-only ID anchors use `getElementById` so Contentful entry IDs that start with a
 * digit remain valid (`querySelector('#5abc…')` throws a SyntaxError).
 *
 * @param {string} selector
 * @returns {Element | null}
 */
const resolveScrollTargetElement = (selector) => {
  if (isHashIdSelector(selector)) {
    return document.getElementById(selector.slice(1));
  }

  try {
    return document.querySelector(selector);
  } catch {
    return null;
  }
};

/**
 * Scrolls the first element matching `selector` so its top sits below visible sticky chrome
 * (delegates to {@link scrollToWithHeaderOffset}).
 *
 * @param {string | string[]} selector - CSS selector (e.g. `'#visitor-info'`, `'.my-section'`), or selectors tried in order until one matches
 * @param {() => void} [onComplete] - Invoked after smooth scroll settles, or immediately if no element matches, the selector is invalid, or not in a browser environment
 * @param {{extraOffsetPx?: number, recheckAfterLayout?: boolean | {followMs?: number, debounceMs?: number}, syncLiveChromeAfterSmoothScroll?: boolean | {behavior?: string, delaysMs?: number[]}, scrollBehavior?: 'auto' | 'smooth'}} [options] - Passed through to {@link scrollToWithHeaderOffset}
 * @returns {boolean} `true` if an element matched and `scrollTo` was run
 *
 * @example
 * scrollElementBelowStickyHeader('#map-hours', () => history.pushState(null, '', '#map-hours'));
 */
export const scrollElementBelowStickyHeader = (selector, onComplete, options = {}) => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    onComplete?.();

    return false;
  }

  const selectors = Array.isArray(selector) ? selector : [selector];

  if (!selectors.length || selectors.some((sel) => !sel || typeof sel !== 'string')) {
    onComplete?.();

    return false;
  }

  for (const sel of selectors) {
    const element = resolveScrollTargetElement(sel);

    if (element) {
      return scrollToWithHeaderOffset(element, onComplete, options);
    }
  }

  onComplete?.();

  return false;
};
